import React, { useState, useContext } from 'react';
import './UserProfileModal.css';
import Modal from '../Modal';
import UserContext from '../../contexts/UserContext';

const UserProfileModal = ({ onClose }) => {
  const { user, updateUser } = useContext(UserContext);

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    const { name, email, currentPassword, newPassword, confirmPassword } = formData;

    // Validation
    if (!name || !email) {
      setError('Name and email are required fields.');
      setLoading(false);
      return;
    }

    // Password validation
    if (newPassword && newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      setLoading(false);
      return;
    }

    // If changing password, current password is required
    if (newPassword && !currentPassword) {
      setError('Current password is required to set a new password.');
      setLoading(false);
      return;
    }

    try {
      // Create payload - only include password fields if updating password
      const payload = {
        id: user?.id,
        name,
        email
      };

      if (newPassword) {
        payload.currentPassword = currentPassword;
        payload.newPassword = newPassword;
      }

      // Call the update user function from context
      const data = await updateUser(payload);
      setSuccess('Profile updated successfully!');
      
      // Reset password fields after successful update
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      
      // Close modal after short delay to show success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Update Profile" onClose={onClose} maxWidth={500}>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Account Information</h3>
            
            <label htmlFor="name">Name*</label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your name"
              required
            />

            <label htmlFor="email">Email Address*</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
              disabled // Email is typically not changeable
            />
            <span className="field-hint">
              Your email is your login and can't be changed here.
            </span>
          </div>

          <div className="form-section">
            <h3>Change Password</h3>
            <p className="section-description">Leave blank if you don't want to change your password</p>

            <label htmlFor="currentPassword">Current Password</label>
            <input
              id="currentPassword"
              name="currentPassword"
              type="password"
              value={formData.currentPassword}
              onChange={handleChange}
              placeholder="Enter your current password"
            />

            <label htmlFor="newPassword">New Password</label>
            <input
              id="newPassword"
              name="newPassword"
              type="password"
              value={formData.newPassword}
              onChange={handleChange}
              placeholder="Enter new password"
            />

            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm new password"
            />
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Updating...' : 'Update Profile'}
            </button>
          </div>
        </form>
    </Modal>
  );
};

export default UserProfileModal;
